/**
 * The form, its draft, and what a save confirms -- once, for both runtimes.
 *
 * `docs/thin-core/liquid-page-runtime.md` §3 and §5. A plugin's page runs this
 * inside the sandbox through `surfacekit/bridge.js`; the shell's own settings
 * and onboarding forms run it in React. §5 says those two are the same
 * behaviour, so they are the same file: this one. The shell imports it as a
 * module, and the bridge carries it inlined (`build_bridge.mjs` copies the text
 * below into the marked region of `bridge.js`, and `bridgeBundle.test.ts`
 * refuses a bridge whose copy has drifted).
 *
 * Nothing here knows how it reaches the world. The three things a runtime has
 * to supply are the projected state to read, a way to hear that it moved, and
 * somewhere to put a draft -- `io` below. Everything else is the rule set:
 *
 * - the business content comes from a declared read; what was typed and not
 *   saved is a draft, and it outlives a reload;
 * - when the read moves, the form shows what it says now with what was typed
 *   kept on top: the fields the person changed stay as typed, the rest follow
 *   the read;
 * - a save confirms the value it submitted and no other, so typing while a
 *   save is in flight cannot be swallowed by that save's answer;
 * - a draft that did not reach disk is not a saved one, and the page must not
 *   say it is.
 */

//: Sorted keys, so that two objects written in a different order compare equal.
function canonical(_key, item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;
  return Object.keys(item).sort().reduce(function (out, key) { out[key] = item[key]; return out; }, {});
}
function equal(left, right) {
  return JSON.stringify(left, canonical) === JSON.stringify(right, canonical);
}
function clone(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}
//: The states a submission has nothing left to say after.
var SETTLED = { completed: 1, failed: 1, stopped: 1 };

function plain(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
/**
 * What was typed over `read`: the keys of an object form whose value is not
 * what was read, or the whole value of any other form.
 */
function typedOver(current, read) {
  if (!plain(current) || !plain(read)) return clone(current);
  return Object.keys(current).reduce(function (out, name) {
    if (!equal(current[name], read[name])) out[name] = clone(current[name]);
    return out;
  }, {});
}
/** What the read says, with what was typed on top of it. */
function withTyped(read, typed) {
  if (plain(read) && plain(typed)) return Object.assign(clone(read), clone(typed));
  return clone(typed);
}

/**
 * Follow a dotted path into a read's whole value (§3's `at`).
 *
 * The same walking rule as the host's own `pluck` (`workbench/server/panels/
 * spec.py::read_version`, `plugins/surface_driver/state.py::pluck`): objects
 * by key, arrays by digit. A path that leads nowhere -- through a scalar, past
 * the end of an array, or to a key that is not there -- is `null`, which is a
 * business state (nothing selected) and not an error.
 */
function valueAt(value, path) {
  var current = value;
  if (!path) return current;
  var parts = path.split(".");
  for (var i = 0; i < parts.length; i++) {
    var part = parts[i];
    if (current && typeof current === "object" && !Array.isArray(current)) {
      current = current[part];
    } else if (Array.isArray(current) && /^\d+$/.test(part)) {
      var at = Number(part);
      current = at >= 0 && at < current.length ? current[at] : null;
    } else {
      return null;
    }
  }
  return current === undefined ? null : current;
}

/** One listener list, told the current value at once and released on stop. */
function listen(list, fn, current, guard) {
  if (typeof fn !== "function") throw new Error("订阅需要一个函数。");
  list.push(fn);
  guard(fn, current(), "pa.subscribe");
  return function () {
    var at = list.indexOf(fn);
    if (at >= 0) list.splice(at, 1);
  };
}

/**
 * One form over one declared read (§3).
 *
 * `options` is what the page declared: which read carries the content
 * (`read`), what to call its draft (`draft`), the name the save tool takes the
 * version under (`version_field`), and which keys of the read are this form's
 * (`fields`). `bound: false` is a form with no business content behind it at
 * all -- an onboarding question, a first-use form -- which starts from
 * `initial`.
 *
 * `io` is the runtime: `snapshot()` reads the projected state, `watch(fn)`
 * calls back whenever it moves and returns a release, `saveDraft(key, entry)`
 * puts a draft somewhere durable (`null` clears it), and `guard(fn, value,
 * where)` runs somebody else's callback without letting it break this one.
 */
function createFormMachine(options, io) {
  options = options || {};
  var index = typeof options.read === "number" ? options.read : 0;
  var key = String(options.draft || ("read-" + index));
  // The name the save tool takes the version under, for a tool that checks
  // nobody moved the content since it was read.  Where the version is *read*
  // from is the plugin's declaration (`PanelSource.version`), never this: a
  // page that picked its own version could confirm a save against a version
  // nobody else ever saw (§3).
  var versionField = options.version_field || options.version || "";
  // Where in the whole read value this form's content sits (dotted path,
  // default the whole thing).  A read that answers `{documents, selected,
  // sha256}` can back a form over just `selected` this way.  The version is
  // NOT walked by this: it is plucked by the host from the WHOLE value, by
  // the plugin's own declared path, never by this page-side one (§3).
  var at = String(options.at || "");
  // Which keys of the read are the ones on this form.  A read answers with
  // everything the page draws -- a summary, a list, a timestamp -- and a save
  // takes the fields it owns; without this the form would submit the whole
  // answer back as arguments the tool never declared.
  var fields = Array.isArray(options.fields) ? options.fields.map(String) : null;
  // A form with nothing read behind it. Not the same as a read that has not
  // answered yet: there is no content coming, so waiting for one would leave
  // the boxes disabled forever.
  var unbound = options.bound === false;
  function narrow(value) {
    if (!fields || !value || typeof value !== "object" || Array.isArray(value)) return value;
    return fields.reduce(function (out, name) {
      if (Object.prototype.hasOwnProperty.call(value, name)) out[name] = value[name];
      return out;
    }, {});
  }
  // `landing`: a save went out and the read has not moved since.  The next
  // move is that save landing, in whatever form the tool stored it.
  var listeners = [], started = false, submitted = null, landing = false, said = "";
  // A save whose answer did not come back: pressing again sends that press
  // again, watched by the listener it already has (`submit`). `null` otherwise.
  var unanswered = null;
  var view = { current: null, business: null, version: null, versioned: false, draft: null, dirty: false,
               saving: false, saved: false, error: null, persisted: true, state: "pending",
               // What this submission is waiting on, while it is still out:
               // `null` once nothing is in flight, otherwise the action's own
               // state word (`submitting | received | running | held |
               // unknown`). `held` is the action's held keys, `[]` otherwise.
               submission: null, held: [] };
  function tell() {
    var signature = JSON.stringify(view, canonical);
    if (signature === said) return;
    said = signature;
    var now = Object.assign({}, view);
    listeners.slice().forEach(function (fn) { io.guard(fn, now, "pa.form"); });
  }
  /** What is on the form, against what was read: an edit is kept, no edit leaves no draft. */
  function settle() {
    view.dirty = !equal(view.current, view.business);
    if (view.dirty) {
      view.draft = clone(view.current);
      write();
    } else if (view.draft !== null) {
      forget();
    }
  }
  function follow() {
    var snapshot = io.snapshot();
    var read = unbound ? { state: "ready", value: options.initial } : (snapshot.reads[index] || { state: "pending" });
    var draft = snapshot.drafts[key];
    view.state = read.state;
    view.error = read.state === "error" ? read.error || "读取失败" : view.error;
    if (read.state === "ready") {
      var content = narrow(valueAt(read.value, at));
      var before = view.business;
      view.business = content;
      var version = read.version === undefined ? null : read.version;
      // No declared version is not a version of `null` that happens to
      // match: it means nobody can tell whether somebody else moved this,
      // and the page is the one that has to say so (§4).
      view.versioned = read.version !== undefined;
      if (!started) {
        started = true;
        view.version = version;
        // What was typed and not saved when the page went away, back on top
        // of what the read says now.
        var kept = Boolean(draft) && draft.value !== undefined;
        view.current = kept ? withTyped(content, draft.value) : clone(content);
        view.dirty = !equal(view.current, content);
        if (view.dirty) view.draft = clone(view.current);
        // A stored draft that says what the content already says is no
        // unsaved edit, and keeping it would only bring it back later.
        else if (kept) forget();
      } else if (!equal(version, view.version)) {
        view.version = version;
        // 已保存 is about this form's own save.  The first move after it went
        // out is that save landing, even when the tool stored the text a
        // little differently (a trailing line break dropped).  A later move
        // that brings other content -- somebody else's edit, a rollback --
        // takes the sentence with it.
        if (landing) landing = false;
        else if (!equal(content, view.current)) view.saved = false;
        // The read moved: show what it says now, with what was typed kept
        // on top of it.
        view.current = view.dirty ? withTyped(content, typedOver(view.current, before)) : clone(content);
        settle();
      }
    }
    // False only for a write KNOWN to have failed. A write still on its way
    // out (`persisted: false, writing: true`) is not a failed one: without
    // `writing`, every keystroke would flash 「草稿没能存下来」 for the length of
    // the round trip, whether or not the write was ever going to fail (§1).
    view.persisted = !(draft && draft.persisted === false && !draft.writing);
    if (draft && draft.error) view.error = draft.error;
    tell();
  }
  var release = io.watch(follow);
  var writing = false, waiting = null;
  /**
   * Nothing unsaved is left: drop the stored draft, and drop any write still
   * queued behind the one in flight.  Without the second half, a quick
   * type-type-undo left a queued write that fired after this clear and put
   * back a draft saying what the content already says.
   */
  function forget() {
    view.draft = null;
    waiting = false;
    io.saveDraft(key, null).catch(function () {});
  }
  function write() {
    view.saved = false;
    // One write in flight at a time, and the last thing typed is what the
    // next one carries: a keystroke is not worth a queue of file writes,
    // and every one of them would be saving something already superseded.
    if (writing) { waiting = true; return; }
    writing = true;
    waiting = false;
    // Only what was typed: a reload puts it back on top of whatever the read
    // says by then, so a field nobody touched here follows the read.
    io.saveDraft(key, { value: typedOver(view.current, view.business) }).then(function () {
      writing = false;
      if (waiting) write();
    }, function (error) {
      writing = false;
      view.persisted = false;
      view.error = String((error && error.message) || error);
      tell();
      if (waiting) write();
    });
  }
  var form = {
    subscribe: function (fn) { return listen(listeners, fn, function () { return Object.assign({}, view); }, io.guard); },
    set: function (patch) {
      // An empty string, `false` and an empty array are values: only an
      // object patch is merged, everything else replaces.
      view.current = patch && typeof patch === "object" && !Array.isArray(patch)
        ? Object.assign({}, view.current, patch) : patch;
      settle();
      tell();
      return view.current;
    },
    submit: function (action, extra) {
      // Pressed again after an answer that never came back: the same press
      // under its own name (the handle sends what it sent), not a save of
      // what is typed now.
      if (unanswered) return unanswered();
      submitted = clone(view.current);
      landing = true;
      var version = view.version;
      view.saving = true;
      view.saved = false;
      view.error = null;
      view.submission = "submitting";
      view.held = [];
      tell();
      var params = Object.assign({}, submitted, extra || {});
      if (versionField) params[versionField] = version;
      // Hand it over first, and only then watch.  A subscription is told
      // the state it finds, and a handle that has saved before is still
      // holding the answer to *that* press: watching first would take the
      // previous answer for this one's, report 已保存 for a save nobody has
      // answered yet, and throw away the draft it was meant to protect.
      var running = action.run(params);
      var stop = null, over = false;
      stop = action.subscribe(function (state) {
        if (over) return;
        if (!SETTLED[state.state]) {
          // What this press is waiting on right now -- held for an approval,
          // still running, an answer that never came back -- so the page can
          // say that rather than a bare 「正在保存」 for however long it takes.
          view.submission = state.state;
          view.held = state.held || [];
          tell();
          return;
        }
        over = true;
        unanswered = null;
        view.saving = false;
        view.submission = null;
        view.held = [];
        if (state.state === "completed") {
          // The save confirms what it submitted.  A second edit made while it
          // was in flight is still unsaved, and its draft stays exactly as it
          // is (§3).
          if (equal(view.current, submitted)) {
            view.dirty = false;
            view.saved = true;
            forget();
          } else {
            view.dirty = true;
          }
        } else {
          view.error = state.error || (state.effects || []).reduce(function (found, effect) {
            return found || effect.error; }, null) || "这次没有保存成功，输入已保留。";
        }
        tell();
        if (stop) stop();
      });
      // The answer cannot have arrived during the line above -- a frame is a
      // message, not a call -- but a settled state read there would have
      // been this press's own, so it is honoured rather than dropped.
      if (over) stop();
      function answered(sending, again) {
        return sending.catch(function (error) {
          if (over) throw error;
          if (error && (error.code === "unknown" || (again && error.code === "surface_paused"))) {
            // No answer is not a no: the save may have landed. A resend the
            // shell could not make just now (paused) leaves it exactly as
            // unsure. The listener above stays, so the record says what
            // became of it.
            view.saving = false;
            view.submission = "unknown";
            unanswered = function () {
              view.saving = true;
              view.submission = "submitting";
              tell();
              return answered(action.run(params), true);
            };
            tell();
            throw error;
          }
          return failed(error);
        });
      }
      function failed(error) {
        over = true;
        unanswered = null;
        view.saving = false;
        view.submission = null;
        view.held = [];
        view.error = String((error && error.message) || error);
        tell();
        if (stop) stop();
        throw error;
      }
      return answered(running);
    },
    state: function () { return Object.assign({}, view); },
    /** Release the runtime subscription. Mounting again does not accumulate. */
    stop: function () { release(); listeners.length = 0; },
  };
  Object.defineProperty(form, "current", { get: function () { return view.current; } });
  Object.defineProperty(form, "business", { get: function () { return view.business; } });
  Object.defineProperty(form, "version", { get: function () { return view.version; } });
  Object.defineProperty(form, "versioned", { get: function () { return view.versioned; } });
  Object.defineProperty(form, "draft", { get: function () { return view.draft; } });
  Object.defineProperty(form, "dirty", { get: function () { return view.dirty; } });
  Object.defineProperty(form, "saving", { get: function () { return view.saving; } });
  Object.defineProperty(form, "saved", { get: function () { return view.saved; } });
  Object.defineProperty(form, "error", { get: function () { return view.error; } });
  Object.defineProperty(form, "persisted", { get: function () { return view.persisted; } });
  Object.defineProperty(form, "submission", { get: function () { return view.submission; } });
  Object.defineProperty(form, "held", { get: function () { return view.held; } });
  follow();
  return form;
}
export { canonical, equal, clone, listen, createFormMachine, SETTLED, valueAt };
